import { Injectable, inject } from '@angular/core';
import { AssetDto, GalleryDto, Section } from '@nusign/picflow-backend-types';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DuplicateAssetsDialogComponent } from '../gallery/dialogs/duplicate-assets-dialog/duplicate-assets-dialog.component';
import { canDisplayAsset } from '../lib/can-display-asset';
import { changeCoverAsset } from '../lib/change-cover-asset';
import { AssetService } from './asset.service';
import { DialogService } from './dialog.service';
import { DuplicateAssetService } from './duplicate-asset.service';
import { GalleryService } from './gallery.service';
import { SectionService } from './section.service';
import { TenantStorageService } from './tenant-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AssetMoveService {
  private sectionService = inject(SectionService);
  private assetService = inject(AssetService);
  private galleryService = inject(GalleryService);
  private dialog = inject(DialogService);
  private duplicateAssetService = inject(DuplicateAssetService);
  private tenantStorageService = inject(TenantStorageService);

  public assetMove$ = new BehaviorSubject<{ assetId: string; movedToGallery: string; oldGalleryId: string }>(undefined);

  async allowCopyAssets(assets: AssetDto[]) {
    const newStorage = assets.reduce((total, asset) => total + asset.version_data.size, 0) / 1024;
    const storage = await firstValueFrom(this.tenantStorageService.getStorage());
    const total = storage.total;
    const used = storage.used;
    const remaining = total - used;
    return newStorage <= remaining;
  }

  private async prepMoveOrCopy(
    _assetsToMove: AssetDto[],
    moveToGallery: GalleryDto,
    action: 'copy' | 'move',
    section?: Section
  ) {
    const [targetSection, allGalleryAssets] = await Promise.all([
      section || firstValueFrom(this.sectionService.getOrCreateSection('asset', moveToGallery.id)),
      this.assetService.getAssetsForGallery(moveToGallery.id),
    ]);

    const duplicateAssets = _assetsToMove.filter(asset => allGalleryAssets.some(a => a.name === asset.name));

    let assetsToMoveOrCopy: AssetDto[] = [];
    let assetsToOverride: { asset: AssetDto; assetToOverride: AssetDto }[] = [];

    if (!duplicateAssets.length) {
      return { targetSection, assetsToMoveOrCopy: _assetsToMove, assetsToOverride };
    }
    const resp = await this.dialog.open(DuplicateAssetsDialogComponent, {
      duplicates: duplicateAssets.map(d => d.name),
      allowedActions: action === 'copy' ? ['skip', 'override', 'new_assets'] : ['skip', 'new_assets'],
      textType: action,
    });

    if (resp === 'skip') {
      assetsToMoveOrCopy = _assetsToMove.filter(asset => !duplicateAssets.includes(asset));
    } else if (resp === 'override') {
      assetsToMoveOrCopy = _assetsToMove.filter(asset => !duplicateAssets.includes(asset));
      assetsToOverride = duplicateAssets.map(asset => {
        const assetToOverride = _assetsToMove.find(_asset => _asset.name === asset.name);
        return { asset, assetToOverride };
      });
    } else if (resp === 'new_assets') {
      assetsToMoveOrCopy = _assetsToMove;
    }

    return { targetSection, assetsToMoveOrCopy, assetsToOverride };
  }

  async copyAssets(_assetsToCopy: AssetDto[], copyToGallery: GalleryDto) {
    if (!(await this.allowCopyAssets(_assetsToCopy))) return false;

    const {
      targetSection,
      assetsToMoveOrCopy: assetsToMove,
      assetsToOverride,
    } = await this.prepMoveOrCopy(_assetsToCopy, copyToGallery, 'copy');

    await this.assetService.copyAssets(
      assetsToMove.map(asset => asset.id),
      copyToGallery.id,
      targetSection.id
    );

    if (!copyToGallery.teaser.uuid && (assetsToMove.length || assetsToOverride.length)) {
      const asset = assetsToMove[0] ?? assetsToOverride[0]?.asset;
      if (canDisplayAsset(asset)) {
        this.autoSetTeaser(copyToGallery, asset);
      }
    }

    if (assetsToMove.length || assetsToOverride.length) return true;
    return false;
  }

  async moveAssets(_assetsToMove: AssetDto[], moveToGallery: GalleryDto, section?: Section) {
    const {
      targetSection,
      assetsToMoveOrCopy: assetsToMove,
      assetsToOverride,
    } = await this.prepMoveOrCopy(_assetsToMove, moveToGallery, 'move', section);

    await Promise.all([
      firstValueFrom(
        this.assetService.updateAssets(assetsToMove, { section: targetSection.id, gallery: moveToGallery.id })
      ),
      ...assetsToOverride.map(data => {
        return this.assetService.makeVersionOutOfAsset(
          data.asset.id,
          data.assetToOverride.id,
          <string>data.assetToOverride.gallery
        );
      }),
    ]);

    assetsToMove.forEach(asset =>
      // this.assetService._updates.next([asset.id, 'asset_moved', moveToGallery.id, <string>asset.gallery])
      this.assetMove$.next({
        assetId: asset.id,
        movedToGallery: moveToGallery.id,
        oldGalleryId: asset.gallery as string,
      })
    );
    assetsToOverride.forEach(
      override =>
        this.assetMove$.next({
          assetId: override.asset.id,
          movedToGallery: moveToGallery.id,
          oldGalleryId: override.assetToOverride.gallery as string,
        })
      // this.assetService._updates.next([asset.asset.id, 'asset_moved', moveToGallery.id, <string>asset.asset.gallery])
    );

    if (!moveToGallery.teaser?.uuid && (assetsToMove.length || assetsToOverride.length)) {
      this.autoSetTeaser(moveToGallery, assetsToMove[0] ?? assetsToOverride[0]?.asset);
    }

    if (assetsToMove.length || assetsToOverride.length) return true;
    return false;
  }

  private async autoSetTeaser(gallery: GalleryDto, asset: AssetDto) {
    changeCoverAsset(gallery.teaser, asset);
    await this.galleryService.updateGallery(gallery, {
      teaser: gallery.teaser,
    });
  }
}
